import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiGet, apiPost, apiPut, apiDelete } from "../../services/api";
import type { Product, ProductCategory } from "../../types";

const EMPTY_FORM = { name: "", description: "", sku: "", barcode: "", categoryId: "", price: "", cost: "", unit: "each", taxable: true, requiresSerialNumber: false, allowCustomReturn: false };

export default function ProductListTab() {
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => { if (orgId) loadData(); }, [orgId]);

  async function loadData() {
    try {
      const [prods, cats] = await Promise.all([
        apiGet<Product[]>(`/organizations/${orgId}/products`),
        apiGet<ProductCategory[]>(`/organizations/${orgId}/categories`),
      ]);
      setProducts(prods); setCategories(cats);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (prod: any) => {
    setEditing(prod.productId);
    setForm({ name: prod.name || "", description: prod.description || "", sku: prod.sku || "", barcode: prod.barcode || "", categoryId: prod.categoryId || "", price: String(prod.price || ""), cost: String(prod.cost || ""), unit: prod.unit || "each", taxable: prod.taxable !== false, requiresSerialNumber: prod.requiresSerialNumber || false, allowCustomReturn: prod.allowCustomReturn || false });
    setShowModal(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      setForm((f) => ({ ...f, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { ...form, price: parseFloat(form.price) || 0, cost: form.cost ? parseFloat(form.cost) : undefined };
    if (editing) {
      await apiPut(`/organizations/${orgId}/products/${editing}`, payload);
    } else {
      await apiPost(`/organizations/${orgId}/products`, payload);
    }
    setShowModal(false);
    await loadData();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this product?")) return;
    await apiDelete(`/organizations/${orgId}/products/${id}`);
    await loadData();
  };

  const getCatName = (id?: string) => categories.find((c) => c.categoryId === id)?.name || "—";

  if (loading) return <div className="empty-state">Loading products...</div>;

  return (
    <div>
      <div className="org-mgmt-header">
        <h2>Products</h2>
        <button className="btn btn-primary" style={{ width: "auto" }} onClick={openCreate}>+ Add Product</button>
      </div>

      {products.length === 0 ? (
        <div className="empty-state"><p>No products yet.</p></div>
      ) : (
        <table className="data-table">
          <thead><tr><th>Name</th><th>SKU</th><th>Barcode</th><th>Category</th><th>Price</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {products.map((prod: any) => (
              <tr key={prod.productId}>
                <td><div style={{ fontWeight: 500 }}>{prod.name}</div>{prod.description && <div style={{ fontSize: "0.8rem", color: "#6b7280" }}>{prod.description}</div>}</td>
                <td><code style={{ fontSize: "0.85rem", background: "#f3f4f6", padding: "0.15rem 0.4rem", borderRadius: "4px" }}>{prod.sku || "—"}</code></td>
                <td><code style={{ fontSize: "0.85rem", background: "#f3f4f6", padding: "0.15rem 0.4rem", borderRadius: "4px" }}>{prod.barcode || "—"}</code></td>
                <td>{getCatName(prod.categoryId)}</td>
                <td>${(prod.price || 0).toFixed(2)}</td>
                <td><span className={`status-badge ${prod.status}`}>{prod.status}</span></td>
                <td><div className="table-actions"><button onClick={() => openEdit(prod)}>Edit</button><button className="delete" onClick={() => handleDelete(prod.productId)}>Delete</button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editing ? "Edit Product" : "Add Product"}</h3>
            <form onSubmit={handleSubmit}>
              <div className="settings-form">
                <div className="form-group"><label>Product Name</label><input name="name" value={form.name} onChange={handleChange} required /></div>
                <div className="form-group"><label>Description</label><input name="description" value={form.description} onChange={handleChange} /></div>
                <div className="form-row">
                  <div className="form-group"><label>SKU</label><input name="sku" value={form.sku} onChange={handleChange} /></div>
                  <div className="form-group"><label>Barcode</label><input name="barcode" value={form.barcode} onChange={handleChange} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Price ($)</label><input name="price" type="number" step="0.01" min="0" value={form.price} onChange={handleChange} required /></div>
                  <div className="form-group"><label>Cost ($)</label><input name="cost" type="number" step="0.01" min="0" value={form.cost} onChange={handleChange} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Category</label><select name="categoryId" value={form.categoryId} onChange={handleChange}><option value="">None</option>{categories.map((c: any) => (<option key={c.categoryId} value={c.categoryId}>{c.name}</option>))}</select></div>
                  <div className="form-group"><label>Unit</label><select name="unit" value={form.unit} onChange={handleChange}><option value="each">Each</option><option value="lb">Pound</option><option value="oz">Ounce</option><option value="kg">Kilogram</option><option value="liter">Liter</option><option value="pack">Pack</option><option value="case">Case</option></select></div>
                </div>
                <div className="form-group"><label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}><input type="checkbox" name="taxable" checked={form.taxable} onChange={handleChange} />Taxable</label></div>
                <div className="form-group"><label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}><input type="checkbox" name="requiresSerialNumber" checked={form.requiresSerialNumber} onChange={handleChange} />Requires Serial Number (at sale and return)</label></div>
                <div className="form-group"><label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}><input type="checkbox" name="allowCustomReturn" checked={form.allowCustomReturn} onChange={handleChange} />Allow custom return (no scan required)</label></div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ width: "auto" }}>{editing ? "Save Changes" : "Add Product"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
